// ============================================================
// TMR Portal Admin — Nova empresa: wizard faseado (cadastro de produtor)
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { StepRail } from '../../components/fields';
import { Icons, StrokeIcon } from '../../lib/icons';
import { useOnboardCompany, useSubdomainSuggestion } from '../../data/companies/queries';
import { useApproveCompanyApplication } from '../../data/companyApplications/queries';
import { APPLICATION_CLOSED_CODE } from '../../data/companyApplications/contract';
import { INITIAL_COMPANY_FORM, companyFormChanged, toOnboardRequest } from '../../data/companies/form';
import type { CompanyForm } from '../../data/companies/form';
import { conflictErrors, conflictField, toFieldConflict } from '../../data/companies/registrationConflict';
import type { FieldConflict } from '../../data/companies/registrationConflict';
import { useDebounced } from '../../useDebounced';
import {
  COMPANY_STEPS,
  StepAddress,
  StepCompanyGeneral,
  StepContactAndRepresentative,
  StepPortalPermissions,
  StepRates,
  StepReceivingAccount,
  StepReview,
} from './steps';
import type { SetField } from './steps';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link NewCompanyModal}. */
interface NewCompanyModalProps {
  onClose: () => void;
  /**
   * Called once the backend accepted the Empresa, with the id it assigned.
   *
   * The modal's own history entry is still the current one here (see {@link useUnsavedChangesGuard}), so the
   * navigation that follows should `replace` it.
   */
  onCreated: (companyId: string) => void;
  /** The registration the wizard opens with — a site application's data (28 §9); empty when omitted. */
  initialForm?: CompanyForm;
  /**
   * Turns the wizard into the approval of a site application: the same steps and validation, but the
   * submit calls `POST /company-applications/{id}/approve` instead of the onboarding, so the Empresa and
   * the application's new status are committed together.
   */
  approval?: { applicationId: string; protocol: string };
}

export function NewCompanyModal({ onClose, onCreated, initialForm, approval }: NewCompanyModalProps) {
  const initial = initialForm ?? INITIAL_COMPANY_FORM;
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [form, setForm] = useState<CompanyForm>(initial);
  const [showErrors, setShowErrors] = useState(false);
  // Once the operator types a domain themselves, the suggestion stops overwriting it.
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  // The CNPJ or subdomain the backend last refused, shown on its field until the operator changes it.
  const [fieldConflict, setFieldConflict] = useState<FieldConflict | null>(null);

  const onboard = useOnboardCompany();
  // Both hooks always run (hooks cannot be conditional); only the one this wizard is for is ever called.
  const approve = useApproveCompanyApplication(approval?.applicationId ?? '');
  const pending = approval ? approve.isPending : onboard.isPending;
  const submitFailure = approval ? approve.error : onboard.error;
  const debouncedName = useDebounced(form.name, 500);
  const suggestion = useSubdomainSuggestion(debouncedName, !subdomainTouched);

  // A registration takes minutes to fill in, and until "Criar empresa" it lives only here: every way out —
  // the close button, the backdrop, Back, a refresh — asks first once there is something in it.
  // A subdomain the suggestion filled in is not something the operator typed, so it is not a loss either — that is
  // what keeps a pre-filled approval from asking about a discard before anybody touched it.
  const baseline = subdomainTouched ? initial : { ...initial, subdomain: form.subdomain };
  const guard = useUnsavedChangesGuard({ dirty: companyFormChanged(baseline, form), onClose });
  // Whether the press that ends in a click on the backdrop also started there. Selecting text in a field and
  // letting go past the panel's edge clicks the backdrop too, and that is not somebody asking to leave.
  const pressStartedOnBackdrop = useRef(false);

  useEffect(() => {
    if (!subdomainTouched && suggestion.data) {
      setForm((f) => ({ ...f, subdomain: suggestion.data.subdomain }));
    }
  }, [suggestion.data, subdomainTouched]);

  const set: SetField = (key, value) => {
    if (key === 'subdomain') setSubdomainTouched(true);
    setForm((f) => ({ ...f, [key]: value }));
  };

  // Both conflict fields live on the first step. A refused value also holds "Avançar": sending it again would
  // only be refused again.
  const conflictOnStep = step === 0 ? conflictErrors(fieldConflict, form) : {};
  const errors = { ...conflictOnStep, ...COMPANY_STEPS[step].validate(form) };
  const hasErrors = Object.keys(errors).length > 0;
  const isLast = step === COMPANY_STEPS.length - 1;

  const goNext = () => {
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    const next = Math.min(step + 1, COMPANY_STEPS.length - 1);
    setStep(next);
    setMaxStep((m) => Math.max(m, next));
  };
  const goBack = () => {
    setShowErrors(false);
    setStep((s) => Math.max(0, s - 1));
  };
  const goTo = (index: number) => {
    if (index <= maxStep) {
      setShowErrors(false);
      setStep(index);
    }
  };

  const onSubmitError = (error: Error) => {
    // Somebody else approved or rejected the application meanwhile: nothing on any step would fix that, so the
    // operator stays where they are and reads the backend's message.
    if (error instanceof ApiError && error.code === APPLICATION_CLOSED_CODE) return;
    // A CNPJ or subdomain someone already has — an Empresa here, or a producer in the legacy — comes back with a
    // code of its own, and the backend's sentence goes on that field. Any other 409 (two submissions racing) or a
    // 400 still takes the operator to the first step, with the message in the banner.
    const conflict = toFieldConflict(error, form);
    setFieldConflict(conflict);
    if (conflict !== null || (error instanceof ApiError && (error.isConflict || error.status === 400))) {
      setStep(0);
      setShowErrors(true);
    }
  };

  const submit = () => {
    const request = toOnboardRequest(form);
    if (approval) {
      approve.mutate(request, { onSuccess: (approved) => onCreated(approved.companyId), onError: onSubmitError });
    } else {
      onboard.mutate(request, { onSuccess: (created) => onCreated(created.companyId), onError: onSubmitError });
    }
  };

  const stepProps = { form, set, errors: showErrors ? errors : conflictOnStep };
  // A failure pinned to a field is not repeated in the banner. Once that field changes, the old message is gone
  // everywhere: it no longer applies.
  const submitError = submitFailure instanceof ApiError && conflictField(submitFailure) === null ? submitFailure : null;

  return (
    <>
      <OverlayPortal>
        <div
          onPointerDown={(e) => {
            pressStartedOnBackdrop.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (pressStartedOnBackdrop.current && e.target === e.currentTarget) guard.requestClose();
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div style={{ width: 940, maxWidth: '100%', maxHeight: '88vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '20px 26px', borderBottom: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="pa-eyebrow" style={{ marginBottom: 4, whiteSpace: 'nowrap' }}>
                  {approval ? `Aprovar solicitação ${approval.protocol}` : 'Nova empresa'} · passo {step + 1} de {COMPANY_STEPS.length}
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 20, margin: 0, letterSpacing: '-0.01em' }}>{COMPANY_STEPS[step].label}</h3>
              </div>
              <button type="button" onClick={guard.requestClose} className="pa-icon-btn" aria-label="Fechar" style={{ width: 34, height: 34 }}>
                <StrokeIcon d={Icons.x} size={18} />
              </button>
            </div>

            <div className="pa-wizard-body" style={{ flex: 1, display: 'flex', minHeight: 0 }}>
              <StepRail steps={COMPANY_STEPS} step={step} maxStep={maxStep} onGo={goTo} />
              <div style={{ flex: 1, overflowY: 'auto', padding: 26 }}>
                {submitError && (
                  <div role="alert" style={{ marginBottom: 18, padding: '12px 14px', background: 'oklch(96% 0.03 28)', border: '1px solid oklch(80% 0.09 28)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'oklch(38% 0.12 28)' }}>
                    {submitError.detail}
                  </div>
                )}
                {step === 0 && (
                  <StepCompanyGeneral
                    {...stepProps}
                    subdomainHint={
                      !subdomainTouched && suggestion.data ? (
                        <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 5 }}>
                          Sugerido a partir do nome — edite se preferir outro.
                        </div>
                      ) : null
                    }
                  />
                )}
                {step === 1 && <StepAddress {...stepProps} />}
                {step === 2 && <StepContactAndRepresentative {...stepProps} />}
                {step === 3 && <StepPortalPermissions {...stepProps} />}
                {step === 4 && <StepReceivingAccount {...stepProps} />}
                {step === 5 && <StepRates {...stepProps} />}
                {step === 6 && <StepReview form={form} goTo={goTo} />}
              </div>
            </div>

            <div style={{ padding: '16px 26px', borderTop: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{isLast ? 'Revise os dados antes de confirmar.' : 'Campos com * são obrigatórios.'}</span>
              <div style={{ display: 'flex', gap: 10 }}>
                {step > 0 && (
                  <Btn variant="secondary" onClick={goBack}>
                    Voltar
                  </Btn>
                )}
                {!isLast && (
                  <Btn variant="primary" icon={Icons.chevronR} onClick={goNext}>
                    Avançar
                  </Btn>
                )}
                {isLast && (
                  <Btn variant="primary" icon={pending ? undefined : Icons.check} onClick={pending ? undefined : submit} disabled={pending}>
                    {approval ? (pending ? 'Aprovando…' : 'Aprovar e criar empresa') : pending ? 'Criando…' : 'Criar empresa'}
                  </Btn>
                )}
              </div>
            </div>
          </div>
        </div>
      </OverlayPortal>

      {/* A sibling of the backdrop, not a child: a click on the dialog's own backdrop must never reach the
          wizard's and read as a second request to leave. */}
      {guard.confirming && (
        <ConfirmDialog
          title="Descartar o cadastro?"
          message={
            approval
              ? 'Os ajustes feitos neste cadastro ainda não foram salvos. Se sair agora, eles serão perdidos; a solicitação continua na fila.'
              : 'Esta nova empresa tem dados preenchidos que ainda não foram salvos. Se sair agora, eles serão perdidos.'
          }
          confirmLabel="Descartar e sair"
          cancelLabel="Continuar editando"
          danger
          icon={Icons.alert}
          onConfirm={guard.discard}
          onClose={guard.keepEditing}
        />
      )}
    </>
  );
}
